import { getNavT, PAGE_PATH_TO_NAV_KEY } from '../locales/navigation';
import { REPORT_PATH_LABELS, REPORT_SECTIONS } from '../components/Reports/reportRouteMeta';
import MainService from './MainService';

const CACHE_KEY = 'ainsoft.allowedPageNames';
const NO_ACCESS_PAGE = '__NO_PAGE_ACCESS__';

const PRIVILEGED_ROLES = new Set(['Admin', 'admin', 'SA', 'CA']);

const pageNameOverrides = {
  '/home/orders/reverse-cancel': 'Reverse Cancelled',
  '/home/expenses/driver-expense': 'Driver Expenses',
  '/home/expenses/company-expense': 'Company Expenses',
  '/home/accounting/account-types': 'Account Type',
  '/home/accounting/chart-of-accounts': 'Chart Of Accounts',
  '/home/accounting/vouchers/cpv': 'Cash Payment',
  '/home/accounting/vouchers/crv': 'Cash Receipt',
  '/home/accounting/vouchers/fpv': 'Fund Payment',
  '/home/accounting/vouchers/frv': 'Fund Receipt',
  '/home/accounting/vouchers/cheque-pv': 'Cheque Payment',
  '/home/accounting/vouchers/cheque-rv': 'Cheque Receipt',
  '/home/accounting/pdc/pending': 'Pending PDCs',
  '/home/accounting/pdc/processed': 'Processed PDCs',
  '/home/accounting/general-ledger': 'General Ledger',
  '/home/accounting/trial-balance': 'Trial Balance',
  '/home/accounting/balance-sheet': 'Balance Sheet',
  '/home/accounting/profit-loss': 'Profit & Loss',
};

const EXTRA_PAGE_DEFINITIONS = [
  { path: '/home/payments/payment-status', pageName: 'Client Payment Status' },
  { path: '/home/returns/return-status', pageName: 'Client Return Status' },
];

const STATIC_ORDER_PATHS = new Set([
  '/home/orders/create-order',
  '/home/orders/order-list',
  '/home/orders/todays-orders',
  '/home/orders/hold-orders',
  '/home/orders/reverse-cancel',
  '/home/orders/batch-print',
]);

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export function readUserInfo() {
  try {
    return JSON.parse(localStorage.getItem('userInfo') || '{}');
  } catch {
    return {};
  }
}

export function isPrivilegedRole(role) {
  return PRIVILEGED_ROLES.has(role);
}

export function clearPageAccessCache() {
  sessionStorage.removeItem(CACHE_KEY);
}

export function getCachedAllowedPages() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function setCachedAllowedPages(pageNames) {
  sessionStorage.setItem(CACHE_KEY, JSON.stringify(pageNames));
}

export function getAllPageDefinitions() {
  const tEnglish = getNavT('en');
  const pages = Object.entries(PAGE_PATH_TO_NAV_KEY).map(([path, navKey]) => ({
    path,
    pageName: pageNameOverrides[path] || tEnglish(navKey),
  }));

  REPORT_SECTIONS.forEach((section) => {
    pages.push({
      path: `/home/reports/${section.id}`,
      pageName: section.navLabel,
    });
    section.cards.forEach((card) => {
      pages.push({
        path: `/home/reports/${card.slug}`,
        pageName: card.label,
      });
    });
  });

  EXTRA_PAGE_DEFINITIONS.forEach((page) => pages.push(page));

  const byPath = new Map();
  pages.forEach((page) => {
    if (!byPath.has(page.path)) byPath.set(page.path, page);
  });
  return Array.from(byPath.values());
}

export function resolvePageNameForPath(path) {
  const normalized = path.replace(/\/+$/, '') || '/home';

  if (PAGE_PATH_TO_NAV_KEY[normalized]) {
    return pageNameOverrides[normalized] || getNavT('en')(PAGE_PATH_TO_NAV_KEY[normalized]);
  }

  if (REPORT_PATH_LABELS[normalized]) {
    return REPORT_PATH_LABELS[normalized];
  }

  const t = getNavT('en');

  if (normalized.startsWith('/home/orders/') && !STATIC_ORDER_PATHS.has(normalized)) {
    return t('orders.orderList');
  }
  if (normalized.startsWith('/home/accounts/addEmployee')) return t('accounts.addEmployee');
  if (normalized.startsWith('/home/accounts/addClient')) return t('accounts.addClient');
  if (normalized.startsWith('/home/accounts/addCustomer')) return t('accounts.addCustomer');
  if (normalized.startsWith('/home/accounts/addAgent')) return t('accounts.addAgent');
  if (normalized.startsWith('/home/vehicle-management/add-edit-vehicle')) return t('vehicle.addEdit');
  if (normalized.startsWith('/home/vehicle-management/vehicle-details')) return t('vehicle.list');
  if (normalized.startsWith('/home/vehicle-management/add-maintenance')) return t('vehicle.addMaintenance');
  if (normalized.startsWith('/home/vehicle-management/add-documents')) return t('vehicle.addDocument');
  if (normalized.startsWith('/home/vehicle-management/add-insurance')) return t('vehicle.addInsurance');
  if (normalized.startsWith('/home/vehicle-management/add-assignment')) return t('vehicle.addAssignment');
  if (normalized.startsWith('/home/vehicle-management/add-fine')) return t('vehicle.addFine');
  if (normalized.startsWith('/home/vehicle-management/fuel-logs')) return t('vehicle.fuelLogs');
  if (normalized.startsWith('/home/vehicle-management/permits')) return t('vehicle.permits');
  if (normalized.startsWith('/home/vehicle-management/inspections')) return t('vehicle.inspections');
  if (normalized.startsWith('/home/vehicle-management/vehicle-expenses')) return t('vehicle.expenses');
  if (normalized.startsWith('/home/vehicle-management/odometer-logs')) return t('vehicle.odometerLogs');
  if (normalized.startsWith('/home/vehicle-management/accidents')) return t('vehicle.accidents');
  if (normalized.startsWith('/home/vehicle-management/service-history')) return t('vehicle.serviceHistory');
  if (normalized.startsWith('/home/shipments/convert-shipment') || normalized.startsWith('/home/shipments/edit-shipment')) {
    return t('shipments.convertShipment');
  }
  if (normalized.startsWith('/home/payments/payment-status/')) {
    return 'Client Payment Status';
  }
  if (normalized.startsWith('/home/returns/return-status/')) {
    return 'Client Return Status';
  }

  let bestMatch = null;
  Object.entries(PAGE_PATH_TO_NAV_KEY).forEach(([prefix, navKey]) => {
    if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
      if (!bestMatch || prefix.length > bestMatch.prefix.length) {
        bestMatch = { prefix, navKey };
      }
    }
  });

  if (bestMatch) {
    return pageNameOverrides[bestMatch.prefix] || t(bestMatch.navKey);
  }

  return null;
}

export function isPathAllowed(path, allowedPageNames) {
  const pageName = resolvePageNameForPath(path);
  if (!pageName) return false;
  return allowedPageNames.includes(pageName);
}

export function getFirstAllowedPath(allowedPageNames) {
  const hit = getAllPageDefinitions().find((page) => allowedPageNames.includes(page.pageName));
  return hit?.path || null;
}

export async function loadAllowedPageNames(userId) {
  const response = await MainService.GetEmployeePageRoleDetails(userId);
  const pages = parseList(response)
    .map((role) => role.PageName)
    .filter((pageName) => pageName && pageName !== NO_ACCESS_PAGE);
  setCachedAllowedPages(pages);
  return pages;
}

export async function ensurePageAccessLoaded(userInfo) {
  if (isPrivilegedRole(userInfo?.Role)) return null;
  const cached = getCachedAllowedPages();
  if (cached) return cached;
  if (!userInfo?.Id) return [];
  return loadAllowedPageNames(userInfo.Id);
}

export function filterMenuItemsByAccess(items, allowedPageNames) {
  if (!Array.isArray(items)) return [];
  if (allowedPageNames === null) return items;
  if (!Array.isArray(allowedPageNames)) return [];
  return items.filter((item) => isPathAllowed(item.path, allowedPageNames));
}
